import type React from 'react';

export const ALLOWED_CONTAINER_STYLE_PROPERTIES = ['backgroundColor', 'border', 'borderRadius', 'padding'] as const;

/** Typen konsumenten forholder seg til. Gir autocomplete pa akkurat det som er tillatt. */
export type MicrowebContainerStyle = Pick<React.CSSProperties, (typeof ALLOWED_CONTAINER_STYLE_PROPERTIES)[number]>;

const ALLOWED = new Set<string>(ALLOWED_CONTAINER_STYLE_PROPERTIES);

/**
 * Tillatte tegn i en kosmetisk css-verdi. Allowlist framfor denylist: alt som ikke
 * trengs for farge, lengde og nokkelord som `solid` blir avvist. Det stenger samtidig
 * kolon, semikolon, klammer, vinkelparenteser og backslash uten a matte ramse dem opp.
 */
const SAFE_VALUE = /^[a-zA-Z0-9\s.,#%()'"-]+$/;

const FORBIDDEN_FUNCTION = /(?:url|image-set|image|expression|element|attr|var|env)\s*\(/i;

const MAX_VALUE_LENGTH = 100;

/**
 * Filtrerer `containerstyle` ned til det som er trygt a bruke.
 */
export const sanitizeContainerStyle = (style: unknown): MicrowebContainerStyle => {
  if (!style || typeof style !== 'object' || Array.isArray(style)) {
    return {};
  }

  const safe: Record<string, string> = {};
  for (const [property, rawValue] of Object.entries(style as Record<string, unknown>)) {
    if (!ALLOWED.has(property)) {
      continue;
    }
    if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
      continue;
    }
    const value = String(rawValue).trim();
    if (!value || value.length > MAX_VALUE_LENGTH || !SAFE_VALUE.test(value) || FORBIDDEN_FUNCTION.test(value)) {
      continue;
    }
    safe[property] = value;
  }

  return safe;
};
